/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1706572800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Jan 30 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "cordial",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Cordial",
          "depth": 0,
          "id": "connect-to-cordial"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Sync mode",
          "depth": 1,
          "id": "sync-mode"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      code: "code",
      h3: "h3",
      ul: "ul",
      li: "li",
      h4: "h4",
      img: "img"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API documentation"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Cordial as contacts"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.cordial.com/hc/en-us/articles/203885958-Contacts-API",
                children: "Contacts"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Supplements"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Cordial's supplemental data sets"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.cordial.com/hc/en-us/articles/204570687-Supplements-API",
                children: "Supplements"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-cordial",
        children: "Connect to Cordial"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Cordial"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Enter your API key from your Cordial account.\nIf you don't have an API key, follow the ", _jsxs(_components.a, {
          href: "https://support.cordial.com/hc/en-us/articles/115005365087",
          children: ["instructions on ", _jsx(_components.strong, {
            children: "Cordial"
          }), " to create API keys"]
        }), ". Ensure the API key has write access for the object you are syncing."]
      }), "\n", _jsx(_components.p, {
        children: "Based on the sync object, ensure your API key has access to the following API endpoints:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Object"
            }), _jsx(_components.th, {
              children: "Required API endpoints"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.code, {
                    children: "GET /accountcontactattributes"
                  })
                }), _jsx("li", {
                  children: _jsx(_components.code, {
                    children: "GET/POST/PUT /contacts"
                  })
                })]
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Supplements"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.code, {
                    children: "GET /supplements"
                  })
                }), _jsx("li", {
                  children: _jsx(_components.code, {
                    children: "GET/POST/PUT /supplements/<supplement key>/records"
                  })
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Next, select which Cordial domain your account uses. This will either be ", _jsx(_components.code, {
          children: "admin.cordial.io"
        }), " or ", _jsx(_components.code, {
          children: "usw2.admin.cordial.io"
        }), ". You can determine which one your account uses by navigating to your Cordial dashboard and checking the website URL."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Cordial destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Cordial destination you want to sync to."]
      }), "\n", _jsx(_components.p, {
        children: "Select which object you'd like to sync."
      }), "\n", _jsx(_components.h3, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new objects to Cordial and updates mapped fields that change in your source."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": updates mapped fields on existing objects in Cordial. New objects are not added."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": pushes new objects to Cordial. Existing objects are not updated."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.h4, {
        id: "contacts",
        children: "Contacts"
      }), "\n", _jsxs(_components.p, {
        children: ["Cordial has the following ", _jsx(_components.a, {
          href: "https://support.cordial.com/hc/en-us/articles/204570347-Accountcontactattributes",
          children: "contact attribute types"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "string"
        }), "\n", _jsx(_components.li, {
          children: "number"
        }), "\n", _jsx(_components.li, {
          children: "date (ISO 8601 standard)"
        }), "\n", _jsx(_components.li, {
          children: "geo"
        }), "\n", _jsx(_components.li, {
          children: "array"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing with all of these types. You may use Hightouch's object inline mapping to construct Cordial's object values from your source."
      }), "\n", _jsxs(_components.p, {
        children: ["You ", _jsx(_components.strong, {
          children: "must"
        }), " create a contact attribute on Cordial before syncing to that field. Follow the ", _jsxs(_components.a, {
          href: "https://support.cordial.com/hc/en-us/articles/115005528368-Contact-attributes#addAttributes",
          children: ["instructions on ", _jsx(_components.strong, {
            children: "Cordial"
          })]
        }), " either via UI or API to create contact attributes."]
      }), "\n", _jsx(_components.h4, {
        id: "supplements",
        children: "Supplements"
      }), "\n", _jsxs(_components.p, {
        children: ["Start syncing supplements by ", _jsx(_components.a, {
          href: "https://support.cordial.com/hc/en-us/articles/115005528468-Supplements#ui",
          children: "creating a supplement collection on Cordial"
        }), ". Indexes on supplement collections are ", _jsx(_components.strong, {
          children: "required"
        }), " mapping fields on Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: "If a field you'd like to map to is not available in the mapping dropdown, you can enter the field name manually:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-cordial-creatable-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.h4, {
        id: "contacts-1",
        children: "Contacts"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " and ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, records must be matched by either Cordial ID or Email. Records without unique IDs will fail to sync."]
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode, records must have an ", _jsx(_components.strong, {
          children: "email field"
        }), " to successfully be added to Cordial. Records without an email identifier will fail to sync."]
      }), "\n", _jsx(_components.h4, {
        id: "supplements-1",
        children: "Supplements"
      }), "\n", _jsxs(_components.p, {
        children: ["Since Cordial does not create ID fields for supplements, ", _jsx(_components.strong, {
          children: "id"
        }), " is required in ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode. In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " and ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, ", _jsx(_components.strong, {
          children: "id"
        }), " is also required to match records. Records without unique IDs will fail to sync."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Cordial ", _jsx(_components.a, {
            href: "https://support.cordial.com/hc/en-us/articles/204570687-Supplements-API#postSuppRecords",
            children: "does not allow"
          }), " 24 character hex strings as ID values for supplements."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
